import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, Sparkles, CreditCard, Users2, Archive, ArrowUpRight } from "lucide-react";
import PublicLayout from "../../components/PublicLayout";
import { Button } from "../../components/ui/button";
import api from "../../lib/api";
import { useAuth } from "../../lib/AuthContext";
import { planLabel } from "../../lib/upgradeCta";

/**
 * Whistle icon — clearly recognizable referee/coach whistle silhouette:
 *   • Large round chamber (the pea housing) — dominates the icon
 *   • Mouthpiece tube extending out to the right
 *   • Lanyard ring + cord clearly above the chamber
 *   • Solid filled pea hole (so it reads at small sizes)
 * Hand-drawn 24x24 viewBox to match lucide stroke conventions; lucide
 * doesn't ship a whistle glyph.
 */
function Whistle({ className }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
      data-testid="trainers-whistle-icon"
    >
      {/* Lanyard ring — sits clearly ABOVE the chamber */}
      <circle cx="7" cy="4" r="2" />
      {/* Cord/connector from ring down to chamber top */}
      <path d="M7 6v3" />
      {/* Main chamber — big round body (the pea housing) */}
      <circle cx="9" cy="14" r="5" />
      {/* Mouthpiece — rounded tube extending out the right side */}
      <path d="M14 12h6a2 2 0 0 1 0 4h-6" />
      {/* Pea / finger hole — solid dot for unambiguous readability */}
      <circle cx="9" cy="14" r="1.3" fill="currentColor" stroke="none" />
    </svg>
  );
}

export default function Pricing() {
  const [brand, setBrand] = useState({ plans: [], pricing_notes: [] });
  const [params] = useSearchParams();
  const recommended = params.get("recommended");
  const { user } = useAuth();
  const isCoach = !!(user && user.role === "coach");
  const planLinkFor = (planId) => (isCoach ? `/coach/settings?plan=${planId}#plan` : "/signup");
  useEffect(() => {
    api.get("/brand").then((r) => setBrand(r.data)).catch(() => setBrand({ plans: [], pricing_notes: [] }));
  }, []);

  // Smooth-scroll to the recommended plan once cards are rendered
  useEffect(() => {
    if (!recommended) return;
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-testid="plan-card-${recommended}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 250);
    return () => clearTimeout(t);
  }, [recommended, brand.plans]);

  const plans = brand.plans || [];
  const trial = plans.find((p) => p.id === "trial");
  const paid = plans.filter((p) => p.id !== "trial");
  const supportEmail = brand.support_email || "support@squadfee.com";
  const ea = !!brand.free_early_access;

  return (
    <PublicLayout>
      <section className="container-page py-16 md:py-20">
        <p className="label-caps">Pricing</p>
        <h1 className="h1 mt-2 max-w-3xl">Simple plans. Parents always free.</h1>
        <p className="mt-4 max-w-2xl text-lg text-ink-700">
          Works for team coaches, personal trainers, instructors, academies, and clubs.
          {ea
            ? " Parents never pay. During Early Access, coaches use Squad Fee free too."
            : " Only the coach / trainer / organizer pays a subscription."}
        </p>

        {/* 14-Day Free Trial — hero card */}
        {trial ? (
          <div
            data-testid="trial-hero"
            className="mt-10 overflow-hidden rounded-3xl border border-brand-100 bg-gradient-to-br from-brand-50 via-white to-white p-8 shadow-sm md:p-10"
          >
            <div className="flex flex-col gap-8 md:flex-row md:items-center md:justify-between">
              <div className="max-w-xl">
                <span className="inline-flex items-center gap-2 rounded-full bg-brand px-3 py-1 text-xs font-bold uppercase tracking-wide text-white">
                  <Sparkles className="h-3.5 w-3.5" /> No credit card required
                </span>
                <h2 className="mt-4 font-heading text-3xl font-bold text-ink-900 sm:text-4xl">
                  {trial.name}
                </h2>
                <p className="mt-2 text-lg text-ink-700">{trial.subtitle}</p>

                <ul className="mt-5 grid gap-2 sm:grid-cols-2">
                  {trial.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-ink-700">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> {f}
                    </li>
                  ))}
                </ul>

                <p className="mt-4 text-sm text-ink-500">
                  {trial.footnote}
                </p>

                <Link to="/signup" className="mt-6 inline-block" data-testid="trial-cta">
                  <Button className="h-12 bg-brand px-6 font-semibold text-white hover:bg-brand-hover">
                    {ea ? trial.cta : "Start 14-Day Free Trial"}
                  </Button>
                </Link>
                <p className="mt-2 text-xs text-ink-500">No credit card required to start.</p>
              </div>
              <div className="hidden md:block">
                <div className="rounded-2xl border border-ink-100 bg-white p-6 shadow-sm">
                  <p className="label-caps">{ea ? "Early Access includes" : "Trial includes"}</p>
                  <p className="mt-2 font-heading text-4xl font-bold text-ink-900">
                    Up to <span className="text-brand">100</span>
                  </p>
                  <p className="text-sm text-ink-500">active players</p>
                  <p className="mt-3 font-heading text-2xl font-bold text-ink-900">
                    Up to <span className="text-brand">4</span> teams
                  </p>
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {/* Paid plans */}
        <h2 className="h2 mt-16" data-testid="paid-plans-heading">{ea ? "Planned pricing after Early Access" : "Paid plans"}</h2>
        <p className="mt-2 max-w-2xl text-sm text-ink-700" data-testid="paid-plans-note">
          {ea
            ? "These plans are not available yet. Early Access users will be notified before paid subscriptions begin, and nobody is charged automatically — you'd choose a plan yourself."
            : "After the 14-day trial ends, choose a paid plan to continue using the app. Switch plans anytime."}
        </p>

        {recommended && !ea ? (
          <div
            data-testid="recommended-banner"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-amber-50 px-4 py-2 text-sm font-semibold text-amber-900 ring-1 ring-amber-200"
          >
            <ArrowUpRight className="h-4 w-4" />
            Recommended for you: <span className="font-bold">{planLabel(recommended)}</span>
          </div>
        ) : null}

        <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {paid.map((p) => {
            const isEnterprise = p.id === "enterprise";
            const isRecommended = recommended === p.id;
            return (
              <div
                key={p.id}
                data-testid={`plan-card-${p.id}`}
                className={`relative flex flex-col rounded-2xl border bg-white p-6 shadow-sm card-hover transition-all ${
                  isRecommended
                    ? "border-amber-400 ring-2 ring-amber-300/60 scale-[1.015]"
                    : p.popular
                    ? "border-brand ring-2 ring-brand/20"
                    : "border-ink-100"
                }`}
              >
                {isRecommended ? (
                  <span
                    data-testid={`plan-card-${p.id}-recommended-badge`}
                    className="absolute -top-3 left-6 inline-flex items-center gap-1 rounded-full bg-amber-500 px-3 py-1 text-xs font-bold uppercase tracking-wide text-white shadow-sm"
                  >
                    <ArrowUpRight className="h-3 w-3" /> Recommended for you
                  </span>
                ) : p.popular ? (
                  <span className="absolute -top-3 left-6 inline-flex items-center gap-1 rounded-full bg-brand px-3 py-1 text-xs font-bold uppercase tracking-wide text-white">
                    <Sparkles className="h-3 w-3" /> Most popular
                  </span>
                ) : null}
                <h3 className="font-heading text-xl font-bold text-ink-900">{p.name}</h3>
                <p className="mt-1 text-xs text-ink-500">{p.best_for}</p>
                <div className="mt-3 flex items-baseline gap-1">
                  {isEnterprise ? (
                    <span className="font-heading text-3xl font-bold text-ink-900">{p.price_label}</span>
                  ) : (
                    <>
                      <span className="font-heading text-4xl font-bold text-ink-900">{p.price_label}</span>
                      <span className="text-sm font-semibold text-ink-500">{p.period}</span>
                    </>
                  )}
                </div>
                <p className="mt-2 text-xs font-bold uppercase tracking-wide text-ink-500">
                  {p.max_teams == null
                    ? "Custom limits"
                    : `Up to ${p.max_teams} team${p.max_teams > 1 ? "s" : ""} and ${p.max_athletes} active players`}
                </p>
                <ul className="mt-5 flex-1 space-y-2 text-sm text-ink-700">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <Check className="mt-0.5 h-4 w-4 text-emerald-600" /> {f}
                    </li>
                  ))}
                </ul>
                <div className="mt-6">
                  {ea && !isEnterprise ? (
                    <p
                      data-testid={`plan-cta-${p.id}`}
                      className="inline-flex h-12 w-full items-center justify-center rounded-md border border-dashed border-ink-200 bg-ink-50 text-sm font-semibold text-ink-500"
                    >
                      Available after Early Access
                    </p>
                  ) : isEnterprise ? (
                    /* Enterprise is contact-only — not self-serve. */
                    <a
                      href={`mailto:${supportEmail}?subject=Squad Fee Enterprise`}
                      data-testid={`plan-cta-${p.id}`}
                      className="inline-flex h-12 w-full items-center justify-center rounded-md border border-ink-300 bg-white font-semibold text-ink-900 hover:bg-ink-100"
                    >
                      Contact Us
                    </a>
                  ) : (
                    <Link to={planLinkFor(p.id)} data-testid={`plan-cta-${p.id}`}>
                      <Button
                        className={`h-12 w-full font-semibold ${
                          isRecommended
                            ? "bg-amber-500 text-white hover:bg-amber-600"
                            : p.popular
                            ? "bg-brand text-white hover:bg-brand-hover"
                            : "bg-white border border-ink-300 text-ink-900 hover:bg-ink-100"
                        }`}
                      >
                        {isRecommended ? `Upgrade to ${p.name}` : isCoach ? `Switch to ${p.name}` : p.cta}
                      </Button>
                    </Link>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Pricing notes */}
        <div className="mt-14 grid gap-4 rounded-2xl border border-ink-100 bg-white p-6 sm:grid-cols-3 md:p-8">
          <div>
            <Users2 className="h-6 w-6 text-brand" />
            <p className="mt-3 font-heading text-base font-bold text-ink-900">Plans are activity-based</p>
            <p className="mt-1 text-sm text-ink-500">
              Plans are based on active players and teams — not just headcount.
            </p>
          </div>
          <div>
            <Archive className="h-6 w-6 text-brand" />
            <p className="mt-3 font-heading text-base font-bold text-ink-900">Archive without penalty</p>
            <p className="mt-1 text-sm text-ink-500">
              Archived players do not count toward your plan limit. Reactivate anytime.
            </p>
          </div>
          <div>
            <Whistle className="h-6 w-6 text-brand" />
            <p className="mt-3 font-heading text-base font-bold text-ink-900">For trainers too</p>
            <p className="mt-1 text-sm text-ink-500">
              For personal trainers and instructors, teams are optional.
              You can manage players individually or in training groups.
            </p>
          </div>
        </div>

        <p className="mt-8 max-w-3xl text-sm text-ink-500" data-testid="pricing-payment-note">
          <CreditCard className="mr-1 inline h-4 w-4 align-[-2px]" />
          {ea
            ? brand.early_access_notice
            : "Payment processing is coming soon. For now, plan selection updates your account but no card is charged."}
        </p>
      </section>
    </PublicLayout>
  );
}
